import { Injectable } from '@angular/core';
import { uniq } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { APIService } from '@fry/lib/api';
import { ApiStore, DBDoc, SearchType, SuperType } from '@fry/lib/store';

import { Role } from './role';
import { SYSTEM_ROLES } from './roles.system';

export interface RolesQuery {
  /**
   * List of role IDs to exclude from the result, excluding system roles
   * can be done using SYSTEM_ROLE_ID constants.
   */
  exclude?: string[],

  /**
   * Should the hidden roles be included in the result, default is false
   */
  hidden?: boolean,

  /**
   * Should the roles be sorted by title in ascending order, default is false
   */
  sort?: boolean;

  /*
  * Should the system roles be included in the result, default is true
  */
  system?: boolean,
}

@Injectable({
  providedIn: 'root'
})
export class RolesStore extends ApiStore<Role> {
  AssociatedModel = Role;

  docType = 'role';
  endpoint = 'roles';
  superType = SuperType.Org;
  searchType = SearchType.LOCAL;

  constructor(api: APIService) {
    super(api);
  }

  public blank(defaults?: Partial<DBDoc>) {
    const doc = this.prepareBlank(defaults);
    return this.createObject(doc);
  };

  allWithSystem(query?: RolesQuery) {
    const options = {...(query ?? {})};
    return this.all().pipe(
      map(roles => {
        const system = SYSTEM_ROLES.filter(role => {
          if (options.hidden !== undefined && role.doc.hidden !== options.hidden) {
            return false;
          }
          if (options.system !== undefined && role.doc.system !== options.system) {
            return false;
          }
          return true;
        });

        // Build unified list of fetched roles & system roles
        let result = [...roles, ...(system.map(r => this.createObject(r.doc)))];

        // Filter out excluded ones, if necessary
        if (options.exclude) {
          result = result.filter(r => options.exclude?.indexOf(r.doc['_id']) === -1);
        }

        // Sort if necessary
        if (options.sort) {
          result.sort((a, b) => a.title.localeCompare(b.title));
        }

        return result;
      })
    );
  }

  public someWithSystem(ids: string[], query?: RolesQuery): Observable<Role[]> {
    return this.allWithSystem(query)
      .pipe(
        map(items => items.filter(item => ids.indexOf(item.doc['_id']) !== -1))
      );
  }

  public findAllDependent() {
    return this.all().pipe(
      map(roles => {
        return roles.filter(r => {
          return r.doc.isUserDependent;
        });
      })
    );
  }

  public withPermission(permission: string): Observable<string[]> {
    return this.byPermission().pipe(
      map(data => {
        return data[permission] || [];
      })
    );
  }

  public withPermissions(permissions: string[]): Observable<string[]> {
    return this.byPermission().pipe(
      map(data => {
        const res = permissions.reduce((cum, cur) => {
          cum = cum.concat(data[cur] || []);
          return cum;
        }, []);

        return uniq(res);
      })
    );
  }

  private byPermission() {
    return this.allWithSystem().pipe(
      map(roles => {
        const byPerm: any = {};
        roles.forEach(role => {
          (role.doc.permissions || []).forEach((perm: string) => {
            if (byPerm[perm] === undefined) {
              byPerm[perm] = [];
            }

            byPerm[perm].push(role.doc._id);
          });
        });
        return byPerm;
      })
    );
  }
}
